/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useRef } from "react";
import { useSelector } from "react-redux";
import type { DataTableProps } from "@/types/DataTableTypes";
import { RootState } from "@/types/RootState";
import TableLoaderSpinner from "@/components/ReactBootstrap/LoaderSpinner/TableLoaderSpinner";
import NoDataContainer from "@/cleanup/containers/NoData/NoDataContainer";
import { useDataFilterTable } from "@/components/SntTableViewCard/hooks/useDataFilterTable";
import { Styles, BottomBar } from "../../styles/TableStyles";
import { useStickyScrollbar, useStickyHeader } from "../../hooks";
import { StickyTableHeader } from "./StickyTableHeader";
import { StickyTableBody } from "./StickyTableBody";
import { SntTablePaging } from "../TableFooter/SntTablePaging";

export const StickyTable = ({
  dataFilter,
  data,
  onConfigColumn,
  isHideConfigColumn,
  onFilterClicked,
  loading,
  noDataText,
  isShowSelectPageSize = true,
  isShowPaging = true,
  isShowDecoratorColor = false,
  isDisabbledGroup = true,
  onRenderConfig,
  onExtraColumnActionClicked,
  isDisabledCopyData = true,
  isDisabbledAddFilter = true,
  restrictedFilters,
  filterVisibleKeys,
  filterMap = {},
}: DataTableProps) => {
  const loginInfo = useSelector((state: RootState) => state.user);
  const stickyHeaderTop = loginInfo?.impersonateData ? 112 : 56;

  const defaultColumn = React.useMemo(
    () => ({
      maxWidth: 600,
    }),
    []
  );

  const table = useDataFilterTable(dataFilter || ({} as any), data, {
    defaultColumn: defaultColumn,
  });

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    totalItems,
    toggleSortBy,
    state: { pageIndex, pageSize },
  } = table;

  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollbarRef, innerRef } = useStickyScrollbar(containerRef);
  useStickyHeader(containerRef);

  const showConfigColumn =
    (!isHideConfigColumn && onConfigColumn) || onRenderConfig;

  // Width the body reserves for the manually-rendered config column
  // (getConfigColumnStyle → 40px). It MUST match what the header reserves
  // (see StickyTableHeader) and MUST be 0 when no config column is rendered.
  // Otherwise the body flex-container is wider than the header, and useFlexLayout
  // spreads that surplus across the columns by flex-grow — visibly widening the
  // widest column (e.g. username) in the body relative to its header cell.
  const configColWidth = showConfigColumn ? 40 : 0;

  const tableProps = getTableProps();
  tableProps.style = tableProps.style || {};
  const existingMinWidth = parseInt(tableProps.style.minWidth || "0", 10);
  tableProps.style.minWidth = existingMinWidth + configColWidth + "px";

  return (
    <div ref={containerRef} style={{ overflowX: "clip", "--sticky-header-top": `${stickyHeaderTop}px` } as React.CSSProperties}>
      <Styles>
        <div className="table-sticky-header">
          <StickyTableHeader
            headerGroups={headerGroups}
            dataFilter={dataFilter}
            rows={rows}
            prepareRow={prepareRow}
            toggleSortBy={toggleSortBy}
            isHideConfigColumn={isHideConfigColumn || false}
            onConfigColumn={onConfigColumn}
            onRenderConfig={onRenderConfig}
            isDisabbledGroup={isDisabbledGroup}
            isDisabledCopyData={isDisabledCopyData}
            onExtraColumnActionClicked={onExtraColumnActionClicked}
            isDisabbledAddFilter={isDisabbledAddFilter}
            onFilterClicked={onFilterClicked}
            restrictedFilters={restrictedFilters}
            filterVisibleKeys={filterVisibleKeys}
            organisationType={loginInfo?.organisation?.organisationType}
          />
        </div>

        <div className="table-container">
          <div {...tableProps} className="table">
            {rows.length === 0 && !loading ? (
              <></>
            ) : (
              <StickyTableBody
                rows={rows}
                prepareRow={prepareRow}
                getTableBodyProps={getTableBodyProps}
                isShowDecoratorColor={isShowDecoratorColor}
                showConfigColumn={!!showConfigColumn}
                onRenderConfig={onRenderConfig}
                isGroupedMode={table.isGroupedMode}
                isDisabbledAddFilter={isDisabbledAddFilter}
                restrictedFilters={restrictedFilters}
                filterVisibleKeys={filterVisibleKeys}
                filterMap={filterMap}
                onFilterClicked={onFilterClicked}
              />
            )}

            {rows.length === 0 && (
              <NoDataContainer noDataText={noDataText} loading={loading} />
            )}
          </div>
        </div>

        <TableLoaderSpinner loading={loading} />
      </Styles>

      <BottomBar>
        <div
          ref={scrollbarRef}
          className="table-sticky-scrollbar"
        >
          <div ref={innerRef} />
        </div>

        {isShowPaging && (
          <div className="table-footer">
            <SntTablePaging
            gotoPage={gotoPage}
            previousPage={previousPage}
            nextPage={nextPage}
            pageCount={pageCount}
            canPreviousPage={canPreviousPage}
            canNextPage={canNextPage}
            pageIndex={pageIndex}
            pageOptions={pageOptions}
            pageSize={pageSize}
            setPageSize={setPageSize}
            isShowSelectPageSize={isShowSelectPageSize}
            totalItems={totalItems}
            headerGroups={headerGroups}
            />
          </div>
        )}
      </BottomBar>
    </div>
  );
};
